import React from 'react';
import { Search, UserCog, Power, Trash2, RotateCcw, Flag, User, UserPlus, LogIn } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { SortableHeader } from './AdminComponents';

// Default avatar component when user has no image
const DefaultAvatar = ({ name, className = "w-8 h-8" }) => {
  const initials = name ? name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2) : '?';
  return (
    <div className={`${className} rounded-full bg-gradient-to-br from-primary/80 to-primary flex items-center justify-center text-white font-medium text-xs`}>
      {initials}
    </div>
  );
};

export const UsersTab = ({ 
  users,
  filteredUsers,
  searchQuery,
  setSearchQuery,
  userFilter,
  setUserFilter,
  sortConfig,
  onSort,
  onPromoteAdmin,
  onRevokeAdmin,
  onToggleActive,
  onDeleteUser,
  onResetPoints,
  onCreateUser,
  onImpersonate
}) => {
  return (
    <div>
      {/* Header with Create User button */}
      <div className="flex flex-wrap gap-4 mb-6 items-center justify-between">
        <div className="flex flex-wrap gap-4 flex-1">
          <div className="flex-1 min-w-[200px]">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
              <Input
                placeholder="Buscar por nombre o email..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
              />
            </div>
          </div>
          <Select value={userFilter} onValueChange={setUserFilter}>
            <SelectTrigger className="w-[180px]">
              <SelectValue placeholder="Filtrar por..." />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos</SelectItem>
              <SelectItem value="admin">Administradores</SelectItem>
              <SelectItem value="teachers">Profesionales</SelectItem>
              <SelectItem value="schools">Escuelas</SelectItem>
              <SelectItem value="disabled">Deshabilitados</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <Button onClick={onCreateUser} className="bg-green-600 hover:bg-green-700">
          <UserPlus className="h-4 w-4 mr-2" />
          Crear Usuario
        </Button>
      </div>
      
      {/* Users Table */}
      <div className="overflow-x-auto rounded-lg border">
        <table className="w-full">
          <thead className="bg-gray-50">
            <tr>
              <SortableHeader label="Usuario" sortKey="name" currentSort={sortConfig} onSort={onSort} />
              <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Roles</th>
              <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Estado</th>
              <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Segnalazioni</th>
              <SortableHeader label="Fecha" sortKey="created_at" currentSort={sortConfig} onSort={onSort} />
              <th className="px-4 py-3 text-right text-sm font-semibold text-gray-700">Acciones</th>
            </tr>
          </thead>
          <tbody className="divide-y">
            {filteredUsers.map(u => (
              <tr key={u.id} className={`hover:bg-gray-50 ${u.is_active === false ? 'bg-red-50' : ''}`}>
                <td className="px-4 py-3">
                  <div className="flex items-center gap-3">
                    {u.avatar ? (
                      <img src={u.avatar} alt="" className="w-8 h-8 rounded-full object-cover" />
                    ) : (
                      <DefaultAvatar name={u.name} className="w-8 h-8" />
                    )}
                    <div>
                      <p className="font-medium text-sm">{u.name}</p>
                      <p className="text-xs text-gray-500">{u.email}</p>
                    </div>
                  </div>
                </td>
                <td className="px-4 py-3">
                  <div className="flex gap-1 flex-wrap">
                    {u.roles?.map(role => (
                      <Badge key={role} variant="outline" className={`text-xs ${role === 'admin' ? 'bg-red-100 text-red-700' : role === 'teacher' ? 'bg-purple-100 text-purple-700' : role === 'school' ? 'bg-blue-100 text-blue-700' : ''}`}>
                        {role}
                      </Badge>
                    ))}
                  </div>
                </td>
                <td className="px-4 py-3">
                  <Badge className={u.is_active !== false ? 'bg-green-500' : 'bg-red-500'}>
                    {u.is_active !== false ? 'Activo' : 'Deshabilitado'}
                  </Badge>
                </td>
                <td className="px-4 py-3">
                  <div className="flex flex-col gap-1">
                    {u.negative_points > 0 && (
                      <div className="flex items-center gap-1">
                        <Badge variant="destructive" className="text-xs">
                          {u.negative_points}/3 punti
                        </Badge>
                        <Button 
                          size="sm" 
                          variant="ghost" 
                          className="h-6 w-6 p-0"
                          onClick={() => onResetPoints(u.id, u.name)}
                          title="Reset punti"
                        >
                          <RotateCcw className="h-3 w-3" />
                        </Button>
                      </div>
                    )}
                    {u.pending_reports_count > 0 && (
                      <Badge variant="outline" className="text-xs bg-yellow-50 text-yellow-700">
                        <Flag className="h-3 w-3 mr-1" />
                        {u.pending_reports_count} segnalazioni
                      </Badge>
                    )}
                    {!u.negative_points && !u.pending_reports_count && (
                      <span className="text-xs text-gray-400">-</span>
                    )}
                  </div>
                </td>
                <td className="px-4 py-3 text-sm text-gray-500">
                  {u.created_at ? new Date(u.created_at).toLocaleDateString('es-ES') : '-'}
                </td>
                <td className="px-4 py-3">
                  <div className="flex gap-1 justify-end">
                    {/* Impersonate button */}
                    <Button 
                      size="sm" 
                      variant="outline"
                      className="text-blue-600 hover:bg-blue-50"
                      onClick={() => onImpersonate(u.id, u.name)}
                      title="Impersonar usuario"
                    >
                      <LogIn className="h-4 w-4" />
                    </Button>
                    {u.roles?.includes('admin') ? (
                      <Button size="sm" variant="outline" className="text-orange-600" onClick={() => onRevokeAdmin(u.id, u.name)} title="Revocar admin">
                        <UserCog className="h-4 w-4" />
                      </Button>
                    ) : (
                      <Button size="sm" variant="outline" onClick={() => onPromoteAdmin(u.id, u.name)} title="Promover a admin">
                        <UserCog className="h-4 w-4" />
                      </Button>
                    )}
                    <Button 
                      size="sm" 
                      variant="outline"
                      className={u.is_active === false ? 'text-green-600' : 'text-orange-600'}
                      onClick={() => onToggleActive(u.id, u.name)}
                      title={u.is_active === false ? 'Activar' : 'Desactivar'}
                    >
                      <Power className="h-4 w-4" />
                    </Button>
                    <Button size="sm" variant="outline" className="text-red-600" onClick={() => onDeleteUser(u.id, u.name)} title="Eliminar">
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p className="text-sm text-gray-500 mt-4">
        Mostrando {filteredUsers.length} de {users.length} usuarios
      </p>
    </div>
  );
};
